import { LoadingButton } from "@mui/lab";
import {
    Alert,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    AccountService,
    SaleActivityDto,
    SaleActivityType,
    UserDto,
} from "../../../api/psc";
import {
    AppTimePicker,
    DialogContainer,
    DialogHeader,
    PrivilegeAlert,
    Spinner,
} from "../../../components/exports";
import { UserAccessTypes } from "../../../components/shared/PrivilegeAlert";
import { DialogContext } from "../../../contexts/DialogueContext";
import { addActivity, updateActivity } from "../../../slices/ActivitySlice";
import { RootState } from "../../../slices/Store";

export default function AddSaleActivityDialog(props: {
    saleId: number;
    parentId?: number;
    activity?: SaleActivityDto;
}) {
    const dispatch = useDispatch();

    const { closeDialog } = useContext(DialogContext);

    const [type, setType] = useState<SaleActivityType>(
        props.activity?.type ?? SaleActivityType.REMINDER
    );
    const [users, setUsers] = useState<UserDto[] | null>(null);
    const [notes, setNotes] = useState(props.activity?.requestNotes ?? "");
    const [dueDate, setDueDate] = useState<string | null>(
        props.activity?.dueDate
    );
    const [assignedUserId, setAssignedUserId] = useState<number | null>(
        props.activity?.assignedUser?.id
    );
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const activitiesState = useSelector(
        (state: RootState) => state.activityState
    );

    const isUpdating = props.activity !== undefined;

    useEffect(() => {
        AccountService.getAccountDetail("1").then((r) => {
            setUsers(r.users);
        });
    }, []);

    const handleNotesChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNotes(event.target.value);
    };

    const handleDueDateChange = (value: string) => {
        setDueDate(value);
    };

    const handleOperatorChange = (event: SelectChangeEvent<number>) => {
        const selectedOperator = users.find((u) => u.id === event.target.value);
        setAssignedUserId(selectedOperator.id);
    };

    const createSaleActivity = async () => {
        setRequestInProgress(true);

        if (isUpdating) {
            const result = await updateActivity(
                props.activity.id,
                {
                    dueDate: dueDate,
                    assignedUserId: assignedUserId,
                    parentId: props.parentId,
                    requestNotes: notes,
                    type: type,
                    status: props.activity.status,
                },
                activitiesState,
                dispatch
            );

            setRequestInProgress(false);

            if (result.error) {
                setError(result.error.getFirstError());
            } else {
                closeDialog();
            }
        } else {
            const result = await addActivity(
                props.saleId,
                {
                    dueDate: dueDate,
                    assignedUserId: assignedUserId,
                    parentId: props.parentId,
                    requestNotes: notes,
                    type: type,
                },
                activitiesState,
                dispatch
            );

            setRequestInProgress(false);

            if (result.error) {
                setError(result.error.getFirstError());
            } else {
                closeDialog();
            }
        }
    };

    if (users === null) {
        return <Spinner />;
    }

    const handleActivityTypeChange = (
        event: React.MouseEvent<HTMLElement>,
        type: SaleActivityType | null
    ) => {
        if (type === null) {
            return;
        }

        setType(type);
    };

    return (
        <DialogContainer>
            <DialogHeader title="افزودن / ویرایش فعالیت" />

            <PrivilegeAlert access={UserAccessTypes.ManageSales} />

            <Stack spacing={2} sx={{ p: 2 }}>
                <ToggleButtonGroup
                    color="primary"
                    value={type}
                    exclusive
                    fullWidth
                    onChange={handleActivityTypeChange}
                >
                    <ToggleButton value={SaleActivityType.REMINDER}>
                        یادآور
                    </ToggleButton>
                    <ToggleButton value={SaleActivityType.TASK}>
                        وظیفه
                    </ToggleButton>
                    <ToggleButton value={SaleActivityType.NOTE}>
                        نظر
                    </ToggleButton>
                </ToggleButtonGroup>

                <TextField
                    value={notes}
                    fullWidth
                    label="توضیحات"
                    multiline
                    onChange={handleNotesChange}
                    rows={3}
                    maxRows={6}
                />

                {type === SaleActivityType.REMINDER && (
                    <AppTimePicker
                        initialValue={dueDate}
                        onChange={handleDueDateChange}
                        label="تاریخ سررسید"
                    />
                )}

                {type === SaleActivityType.TASK && (
                    <FormControl fullWidth>
                        <InputLabel>کارشناس فروش</InputLabel>

                        <Select
                            labelId="select-operator-label"
                            label="کارشناس فروش"
                            value={assignedUserId}
                            onChange={handleOperatorChange}
                        >
                            {users.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.displayName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                <LoadingButton
                    loading={requestInProgress}
                    onClick={createSaleActivity}
                    variant="contained"
                >
                    {!isUpdating ? "افزودن فعالیت" : "ویرایش فعالیت"}
                </LoadingButton>
            </Stack>
        </DialogContainer>
    );
}
